/**
 * Shared state for the problem text of a page or a panel.
 *
 * A mutation that fails writes the readable message into `problem`. The page
 * shows it in a `Problem` block and clears it on the next action.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { problemText } from "./api.ts";

export interface ProblemState {
  readonly problem: string | null;
  readonly setProblem: (problem: string | null) => void;
  /** Record the readable message of a failed request. */
  readonly onError: (error: unknown) => void;
  /** Remove the problem text. */
  readonly clear: () => void;
}

/** Hold the problem text of one page or panel. */
export function useProblem(): ProblemState {
  const [problem, setProblem] = useState<string | null>(null);
  const onError = useCallback((error: unknown): void => setProblem(problemText(error)), []);
  const clear = useCallback((): void => setProblem(null), []);
  return { problem, setProblem, onError, clear };
}

/** Return a function that invalidates these query keys, one after the other. */
export function useRefresh(keys: readonly (readonly unknown[])[]): () => Promise<void> {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    for (const queryKey of keys) {
      await queryClient.invalidateQueries({ queryKey: [...queryKey] });
    }
  };
}
